import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { UserCircle, ArrowRight } from "@phosphor-icons/react";

const LABELS = { name: "Full name", email: "Email", mobile: "Mobile number", role: "Role", institution: "Institution" };
const DISMISS_KEY = "profile_reminder_dismissed";

export default function ProfileReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const incomplete = user && user.role !== "super_admin" && user.profile_complete === false;

  useEffect(() => {
    if (incomplete && !sessionStorage.getItem(DISMISS_KEY)) setOpen(true);
  }, [incomplete]);

  if (!incomplete) return null;
  const missing = (user.profile_missing || []).map((k) => LABELS[k] || k);
  const pct = user.profile_percent ?? 0;

  const skip = () => { sessionStorage.setItem(DISMISS_KEY, "1"); setOpen(false); };
  const go = () => { setOpen(false); navigate("/complete-profile"); };

  return (
    <>
      <div className="mb-6 border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="profile-reminder-banner">
        <UserCircle size={20} className="text-amber-700" />
        <div className="flex-1 min-w-[200px] text-sm text-amber-900">
          Your profile is <span className="font-semibold" data-testid="profile-reminder-percent">{pct}%</span> complete.
          {missing.length > 0 && <span className="text-amber-800"> Missing: {missing.join(", ")}.</span>}
        </div>
        <button onClick={go} className="bg-zinc-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800 transition-colors" data-testid="profile-reminder-banner-btn">
          Complete profile
        </button>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : skip())}>
        <DialogContent className="rounded-none" data-testid="profile-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Finish setting up your profile</DialogTitle>
            <DialogDescription>Your profile is {pct}% complete. Add the missing details so we can send results and certificates to the right place.</DialogDescription>
          </DialogHeader>
          <div className="h-2 bg-zinc-100 w-full">
            <div className="h-2 bg-zinc-900 transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
          <ul className="text-sm text-zinc-700 space-y-1" data-testid="profile-reminder-missing-list">
            {missing.map((m) => <li key={m} className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-amber-500" />{m}</li>)}
          </ul>
          <div className="flex gap-2 pt-2">
            <button onClick={skip} className="flex-1 border border-zinc-300 py-2.5 text-sm font-medium hover:bg-zinc-50 transition-colors" data-testid="profile-reminder-skip-btn">
              Skip for now
            </button>
            <button onClick={go} className="flex-1 bg-zinc-900 text-white py-2.5 text-sm font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 transition-colors" data-testid="profile-reminder-complete-btn">
              Complete now <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
